// Pure cart helpers (no React, no storage) so the rules that protect a
// shopper's bag can be unit-tested on their own.

/** PlazaStack limits: 1-10 per line, 1-20 lines per order. */
export const MAX_QTY_PER_LINE = 10;
export const MAX_LINES = 20;

export interface BoughtLine { variantId: string; qty: number }

/**
 * After a paid order, drop the lines that order bought. Conservative on
 * purpose: a line goes only if it is still in the bag with EXACTLY the
 * quantity that was bought. A line the shopper changed since (another tab, a
 * later visit) is left alone; nothing is ever decremented, so running this
 * twice for the same order cannot take more than once. A stray leftover item
 * is a small annoyance; deleting something the shopper still wants is not.
 */
export function removeBought<T extends { variantId: string; qty: number }>(lines: T[], bought: BoughtLine[]): T[] {
  const exact = new Map(bought.map((b) => [b.variantId, b.qty]));
  return lines.filter((l) => exact.get(l.variantId) !== l.qty);
}

/**
 * Can this add go through? Never reduces an existing quantity: if the new total
 * would exceed capacity the add is refused and the line is left as it is (a
 * line already over capacity keeps its shortage flag until the shopper lowers it).
 */
export function planAdd(existingQty: number | null, add: number, maxQty: number): { ok: true; qty: number } | { ok: false } {
  const cap = lineCap({ maxQty });
  const total = (existingQty ?? 0) + add;
  return total > cap || add < 1 ? { ok: false } : { ok: true, qty: total };
}

/** Highest quantity the shopper may hold for a line. */
export function lineCap(l: { maxQty: number }): number {
  return Math.max(1, Math.min(MAX_QTY_PER_LINE, Math.floor(l.maxQty) || MAX_QTY_PER_LINE));
}

/** True when the requested quantity is more than can be ordered (or not a valid quantity at all). */
export function overCap(l: { qty: number; maxQty: number }): boolean {
  return !Number.isInteger(l.qty) || l.qty < 1 || l.qty > lineCap(l);
}

/** Units of a line that will ship as pre-order (those beyond what is in stock). */
export function preorderUnits(l: { qty: number; preorder: boolean; inStockQty?: number }): number {
  return l.preorder ? Math.max(0, l.qty - (l.inStockQty ?? 0)) : 0;
}
